/**
 * This config needs to be separate from ./i18n.ts in order to support __mocks__/react-i18next.tsx
 */
import { getAppAssetUrl } from '@clover-microapp/utils-browser';
import { InitOptions } from 'i18next';
import HttpApi from 'i18next-http-backend';
import numeral from 'numeral';
import { Environment } from 'src/utils/Environment';
import { getDataCenter, getDefaultLocale } from 'src/utils/Locales';

import 'numeral/locales/de';
import 'numeral/locales/es';
import 'numeral/locales/fr-ca';
import { formatter as centsFormatter } from './utils/CentsFormatter';

function configureNumeralLocale(lng?: string) {
  switch (lng) {
    case 'fr':
    case 'fr-CA':
      numeral.locale('fr-ca');
      break;

    case 'es':
    case 'es-AR':
      numeral.locale('es');
      break;

    case 'de':
    case 'de-DE':
      numeral.locale('de');

    case 'en':
    case 'en-US':
    default:
      numeral.locale('en');
      break;
  }
}

numeral.register('format', 'cents', centsFormatter('¢', 100));

const CURRENT_DATA_CENTER = getDataCenter();
export const CURRENT_I18N_KEY = `${CURRENT_DATA_CENTER.code}.Locale`;

const BACKENDS = new Map();

BACKENDS.set(HttpApi, {
  loadPath: getAppAssetUrl(`${process.env.REACT_APP_ASSET_PATH}static/src/locales/{{lng}}/{{ns}}.json`),
  allowMultiLoading: false,
  queryStringParams: LOCALE_VERSIONS, // prevent nginx from serving old cached version
});

export const baseConfig: InitOptions = {
  // this should match contentful
  supportedLngs: [
    'en',
    'en-CA',
    'en-IE',
    'en-GB',
    'en-US',
    'fr-CA',
    'de-DE',
    'es-AR',
  ],
  fallbackLng: getDefaultLocale().code, // Get the default locale code for the current data center

  debug: Environment.current.isLocalhost,

  ns: ['common'],
  defaultNS: 'common',

  // NOTE: do not add a 'lng' value because it will prevent any detection logic from running

  detection: {
    // order and from where user language should be detected
    order: ['querystring', 'localStorage', 'navigator', 'cookie', 'htmlTag', 'path', 'subdomain'],

    // keys or params to lookup language from
    lookupQuerystring: 'lng',
    lookupLocalStorage: CURRENT_I18N_KEY,
    lookupCookie: 'i18next',

    // Do not cache the user's selected language
    caches: [],
  },

  backend: {
    backends: Array.from(BACKENDS.keys()),
    backendOptions: Array.from(BACKENDS.values()),
  },

  // explicitly set to false for usage with react
  interpolation: {
    escapeValue: false,
    format(value, format, lng) {
      configureNumeralLocale(lng);

      if (typeof value === 'number' && format) {
        return numeral(value).format(format);
      }

      return value;
    },
  },

  react: { useSuspense: true },
};
